import { showHasEnded, showIsSoldOut, isAfterShowIntro } from 'utils/show'

import LiveNoAccess from './LiveNoAccess/LiveNoAccess'
import LiveStreamActive from './LiveStreamActive/LiveStreamActive'
import LiveStreamError from './LiveStreamError/LiveStreamError'
import LiveStreamIdle from './LiveStreamIdle/LiveStreamIdle'
import LoadingAnimation from './LoadingAnimation/LoadingAnimation'
import PreShowPreRoll from './PreShowPreRoll/PreShowPreRoll'
import React from 'react'
import VideoOnDemand from './VideoOnDemand/VideoOnDemand'
import { VideoStatus } from 'data/Video'
import { isLiveStreamActive } from 'utils/video'
import { InteractionPlayerProps } from './InteractionPlayer'
import { PlayerStates } from 'data/VideoPlayer'
import { isCurtainClosing } from 'utils/curtain'
import PlayerWrapperWithPoster from './PlayerWrapperWithPoster/PlayerWrapperWithPoster'
import { ScopeType } from 'data/Show'

interface Transition {
    test: (props: InteractionPlayerProps) => boolean
    newState: PlayerStates
}
interface PlayerState {
    name: PlayerStates
    render: React.FC<InteractionPlayerProps>
    autoTransitions?: Transition[]
}

const playerStates: { [s in ScopeType]: PlayerState[] } = {
    [ScopeType.PUBLIC]: [
        {
            // Renders the hype video
            name: PlayerStates.PRE_SHOW_W_PRE_ROLL,
            render: props => (
                <PreShowPreRoll passList={props.passList} hypeVideoVOD={props.show.hypeVideoVOD!} show={props.show} />
            ),
            autoTransitions: [
                {
                    test: props => !props.userHasPurchasedTicket && showIsSoldOut(props.remainingPasses),
                    // Renders the live video blurred and tell the user that the show is sold out.
                    newState: PlayerStates.SOLD_OUT,
                },
                {
                    test: props =>
                        (isAfterShowIntro(props.show.startTime) && isLiveStreamActive(props.video)) || props.forceLive,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => !props.userHasPurchasedTicket && !props.showHasStarted && !props.shouldPlayPreRoll,
                    // Render an image explaining to the user that the show is about to start and he/she should purchase the ticket.
                    newState: PlayerStates.PRE_SHOW_NO_ACCESS,
                },
                {
                    test: props => props.userHasPurchasedTicket && !props.showHasStarted && !props.shouldPlayPreRoll,
                    // Render an image explaining to the user that the show is about to start.
                    newState: PlayerStates.PRE_SHOW_W_ACCESS,
                },
                {
                    test: props =>
                        !props.userHasPurchasedTicket &&
                        props.showHasStarted &&
                        !props.shouldPlayPreRoll &&
                        props.previewStarted,
                    // Is time to go live with a preview sample!
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => props.userHasPurchasedTicket && props.showHasStarted && !props.shouldPlayPreRoll,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
        {
            // Renders an image saying that the show is about to start
            // and we asks the user to purchase the ticket.
            name: PlayerStates.PRE_SHOW_NO_ACCESS,
            render: props => (
                <PlayerWrapperWithPoster posterImg={props.show.posterImg} stateId={PlayerStates.PRE_SHOW_NO_ACCESS} />
            ),
            autoTransitions: [
                {
                    test: props => !props.userHasPurchasedTicket && showIsSoldOut(props.remainingPasses),
                    // Renders the live video blurred and tell the user that the show is sold out.
                    newState: PlayerStates.SOLD_OUT,
                },
                {
                    test: props =>
                        (isAfterShowIntro(props.show.startTime) && isLiveStreamActive(props.video)) || props.forceLive,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => props.userHasPurchasedTicket && !props.showHasStarted,
                    // Renders an image saying that the show is about to start.
                    newState: PlayerStates.PRE_SHOW_W_ACCESS,
                },
                {
                    test: props => props.showHasStarted,
                    // It is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
        {
            // Renders an image saying that the show is about to start.
            name: PlayerStates.PRE_SHOW_W_ACCESS,
            render: props => (
                <PlayerWrapperWithPoster posterImg={props.show.posterImg} stateId={PlayerStates.PRE_SHOW_W_ACCESS} />
            ),
            autoTransitions: [
                {
                    test: props =>
                        (isAfterShowIntro(props.show.startTime) && isLiveStreamActive(props.video)) ||
                        props.forceLive ||
                        props.showHasStarted,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
        {
            // Live mode baby!!!
            name: PlayerStates.LIVE,
            render: props => {
                const videoPlayerContent = videoPlayerContentSuppliers[props.video.status]
                return videoPlayerContent ? (
                    videoPlayerContent({ ...props, muted: process.env.NODE_ENV === 'development' })
                ) : (
                    <LoadingAnimation />
                )
            },
            autoTransitions: [
                {
                    test: props => !props.userHasPurchasedTicket && showIsSoldOut(props.remainingPasses),
                    // Renders the live video blurred and tell the user that the show is sold out.
                    newState: PlayerStates.SOLD_OUT,
                },
                {
                    test: props => !props.userHasPurchasedTicket && props.previewEnded,
                    // Render the live video blurred.
                    newState: PlayerStates.LIVE_NO_ACCESS,
                },
                {
                    // TODO: we need to get a ws event to update the endTime value, otherwise this state will never be reached here.
                    test: props => showHasEnded(props.show),
                    // Render the hype video
                    newState: PlayerStates.SHOW_ENDED,
                },
            ],
        },
        {
            // Renders the live video blurred.
            name: PlayerStates.LIVE_NO_ACCESS,
            render: props => (
                <LiveNoAccessWrapper
                    {...props}
                    header="YOUR FREE PREVIEW HAS ENDED"
                    subheader="Please register to continue watching!"
                    stateId={PlayerStates.LIVE_NO_ACCESS}
                />
            ),
            autoTransitions: [
                {
                    test: props => !props.userHasPurchasedTicket && showIsSoldOut(props.remainingPasses),
                    // Render the live video blurred with a sold out message.
                    newState: PlayerStates.SOLD_OUT,
                },
                {
                    test: props => props.userHasPurchasedTicket,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
        {
            // Renders the live video blurred and tell the user that the show is sold out.
            name: PlayerStates.SOLD_OUT,
            render: props => (
                <LiveNoAccessWrapper
                    {...props}
                    header="THE EVENT HAS SOLD OUT!"
                    subheader="Create an account to stay in the loop for all of our upcoming shows"
                    stateId={PlayerStates.SOLD_OUT}
                />
            ),
        },
        {
            // Renders the hype video
            name: PlayerStates.SHOW_ENDED,
            render: props => {
                if (props.videoOnDemand && props.videoOnDemand.playbackId) {
                    return (
                        <VideoOnDemand
                            title={props.videoOnDemand.title}
                            playbackId={props.videoOnDemand.playbackId}
                            passList={props.passList}
                            stateId={PlayerStates.SHOW_ENDED}
                            videoStartTime={props.videoOnDemand.startTime}
                            show={props.show}
                        />
                    )
                }

                return (
                    <PlayerWrapperWithPoster
                        posterImg={props.show.posterImg}
                        stateId={`${PlayerStates.SHOW_ENDED}-poster`}
                    />
                )
            },
        },
        {
            // Renders the Loading animation
            name: PlayerStates.LOADING,
            render: () => <LoadingAnimation />,
            autoTransitions: [
                {
                    test: props => showHasEnded(props.show),
                    // Render the hype video
                    newState: PlayerStates.SHOW_ENDED,
                },
                {
                    test: props => !props.userHasPurchasedTicket && showIsSoldOut(props.remainingPasses),
                    // Render the live video blurred with a sold out message.
                    newState: PlayerStates.SOLD_OUT,
                },
                {
                    test: props =>
                        !props.showHasStarted &&
                        isAfterShowIntro(props.show.startTime) &&
                        isLiveStreamActive(props.video),
                    // Renders Live Video
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => !props.showHasStarted && props.shouldPlayPreRoll,
                    // Renders the hype video
                    newState: PlayerStates.PRE_SHOW_W_PRE_ROLL,
                },
                {
                    test: props => !props.userHasPurchasedTicket && !props.showHasStarted && !props.shouldPlayPreRoll,
                    // Render an image explaining to the user that the show is about to start and he/she should purchase the ticket.
                    newState: PlayerStates.PRE_SHOW_NO_ACCESS,
                },
                {
                    test: props => props.userHasPurchasedTicket && !props.showHasStarted && !props.shouldPlayPreRoll,
                    // Render an image explaining to the user that the show is about to start.
                    newState: PlayerStates.PRE_SHOW_W_ACCESS,
                },
                {
                    test: props => !props.userHasPurchasedTicket && props.showHasStarted && props.previewStarted,
                    // Is time to go live with a preview sample!
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => props.userHasPurchasedTicket && props.showHasStarted,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => !props.userHasPurchasedTicket && props.showHasStarted && props.previewEnded,
                    // Render the live video blurred.
                    newState: PlayerStates.LIVE_NO_ACCESS,
                },
            ],
        },
    ],
    [ScopeType.PRIVATE]: [
        {
            // Renders the hype video
            name: PlayerStates.PRE_SHOW_W_PRE_ROLL,
            render: props => (
                <PreShowPreRoll passList={props.passList} hypeVideoVOD={props.show.hypeVideoVOD!} show={props.show} />
            ),
            autoTransitions: [
                {
                    test: props =>
                        (isAfterShowIntro(props.show.startTime) && isLiveStreamActive(props.video)) || props.forceLive,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => props.userHasPurchasedTicket && !props.showHasStarted && !props.shouldPlayPreRoll,
                    // Render an image explaining to the user that the show is about to start.
                    newState: PlayerStates.PRE_SHOW_W_ACCESS,
                },
                {
                    test: props => props.userHasPurchasedTicket && props.showHasStarted && !props.shouldPlayPreRoll,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
        {
            // Renders an image saying that the show is about to start.
            name: PlayerStates.PRE_SHOW_W_ACCESS,
            render: props => (
                <PlayerWrapperWithPoster posterImg={props.show.posterImg} stateId={PlayerStates.PRE_SHOW_W_ACCESS} />
            ),
            autoTransitions: [
                {
                    test: props =>
                        (isAfterShowIntro(props.show.startTime) && isLiveStreamActive(props.video)) ||
                        props.forceLive ||
                        props.showHasStarted,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
        {
            // Live mode baby!!!
            name: PlayerStates.LIVE,
            render: props => {
                const videoPlayerContent = videoPlayerContentSuppliers[props.video.status]
                return videoPlayerContent ? (
                    videoPlayerContent({ ...props, muted: process.env.NODE_ENV === 'development' })
                ) : (
                    <LoadingAnimation />
                )
            },
            autoTransitions: [
                {
                    // TODO: we need to get a ws event to update the endTime value, otherwise this state will never be reached here.
                    test: props => showHasEnded(props.show),
                    // Render the hype video
                    newState: PlayerStates.SHOW_ENDED,
                },
            ],
        },
        {
            // Renders the hype video
            name: PlayerStates.SHOW_ENDED,
            render: props => {
                if (props.videoOnDemand && props.videoOnDemand.playbackId) {
                    return (
                        <VideoOnDemand
                            title={props.videoOnDemand.title}
                            playbackId={props.videoOnDemand.playbackId}
                            passList={props.passList}
                            stateId={PlayerStates.SHOW_ENDED}
                            videoStartTime={props.videoOnDemand.startTime}
                            show={props.show}
                        />
                    )
                }

                return (
                    <PlayerWrapperWithPoster
                        posterImg={props.show.posterImg}
                        stateId={`${PlayerStates.SHOW_ENDED}-poster`}
                    />
                )
            },
        },
        {
            // Renders the Loading animation
            name: PlayerStates.LOADING,
            render: () => <LoadingAnimation />,
            autoTransitions: [
                {
                    test: props => showHasEnded(props.show),
                    // Render the hype video
                    newState: PlayerStates.SHOW_ENDED,
                },
                {
                    test: props =>
                        !props.showHasStarted &&
                        isAfterShowIntro(props.show.startTime) &&
                        isLiveStreamActive(props.video),
                    // Renders Live Video
                    newState: PlayerStates.LIVE,
                },
                {
                    test: props => !props.showHasStarted && props.shouldPlayPreRoll,
                    // Renders the hype video
                    newState: PlayerStates.PRE_SHOW_W_PRE_ROLL,
                },
                {
                    test: props => props.userHasPurchasedTicket && !props.showHasStarted && !props.shouldPlayPreRoll,
                    // Render an image explaining to the user that the show is about to start.
                    newState: PlayerStates.PRE_SHOW_W_ACCESS,
                },
                {
                    test: props => props.userHasPurchasedTicket && props.showHasStarted,
                    // Is time to go live!
                    newState: PlayerStates.LIVE,
                },
            ],
        },
    ],
}

interface LiveNoAccessWrapperProps extends InteractionPlayerProps {
    header: string
    subheader: string
    stateId: string
}

const LiveNoAccessWrapper: React.FC<LiveNoAccessWrapperProps> = props => {
    // This is live mode blurred.
    const videoPlayerContent = videoPlayerContentSuppliers[props.video.status]
    return (
        <LiveNoAccess header={props.header} subheader={props.subheader} stateId={props.stateId}>
            {videoPlayerContent ? videoPlayerContent({ ...props, muted: true }) : <LoadingAnimation />}
        </LiveNoAccess>
    )
}

interface VideoPlayerContentSuppliersProps extends InteractionPlayerProps {
    muted: boolean
}
const videoPlayerContentSuppliers: { [k in VideoStatus]: React.FC<VideoPlayerContentSuppliersProps> } = {
    [VideoStatus.UNKNOWN]: () => {
        console.error('Player with Unknown state trying to render')
        return null
    },
    [VideoStatus.STREAMING]: props => (
        <PlayerWrapperWithPoster posterImg={props.show.posterImg} stateId={`${PlayerStates.LIVE}_streaming`} />
    ),
    [VideoStatus.ACTIVE]: props => (
        // This is live mode: You will see the video stream
        <LiveStreamActive show={props.show} passList={props.passList} muted={props.muted} />
    ),
    [VideoStatus.IDLE]: props => {
        if (isCurtainClosing(props.curtainValue)) {
            return <PlayerWrapperWithPoster posterImg={props.show.posterImg} stateId={`${PlayerStates.LIVE}_idle`} />
        }
        return (
            // This is live mode where there is no active video stream.
            <LiveStreamIdle show={props.show} />
        )
    },
    [VideoStatus.ERROR]: props => (
        <LiveStreamError userHasPurchasedTicket={props.userHasPurchasedTicket} posterImg={props.show.posterImg} />
    ),
}

export default playerStates
